import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Check, X, Flag, Eye, Search, Star, Bot, RefreshCw, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import AdminLayout from './AdminLayout';
import type { Review } from '@shared/schema';

interface AIModerationScores {
  text?: {
    hate: number;
    harassment: number;
    selfHarm: number;
    sexual: number;
    violence: number;
    profanity: number;
    spam: number;
  };
  images?: Array<{
    flagged: boolean;
    scores?: Record<string, number>;
  }>;
}

interface AdminReview extends Review {
  user?: { username: string; displayName: string | null };
  restaurant?: { name: string };
}

function ScoreBar({ label, score }: { label: string; score: number }) {
  const percentage = Math.round(score * 100);
  const getColor = () => {
    if (percentage > 50) return 'bg-red-500';
    if (percentage > 25) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-20 text-muted-foreground capitalize">{label}:</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor()} transition-all`} 
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-8 text-right">{percentage}%</span>
    </div>
  );
}

export default function AdminReviews() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());

  const toggleExpanded = (reviewId: string) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  const { data: reviews, isLoading } = useQuery<AdminReview[]>({
    queryKey: ['/api/admin/reviews', statusFilter],
  });

  const { data: reportsData } = useQuery<{ reports: Array<{ targetId: string; targetType: string }> }>({
    queryKey: ['/api/admin/reports?status=pending&targetType=review'],
  });

  const reviewReportCounts = new Map<string, number>();
  reportsData?.reports?.forEach(report => {
    if (report.targetType === 'review') {
      reviewReportCounts.set(report.targetId, (reviewReportCounts.get(report.targetId) || 0) + 1);
    }
  });

  const moderateMutation = useMutation({
    mutationFn: async ({ reviewId, status, notes }: { reviewId: string; status: string; notes?: string }) => {
      return apiRequest('PATCH', `/api/admin/reviews/${reviewId}/moderate`, { status, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reviews'] });
      toast({ title: 'Review updated', description: 'Moderation status has been updated' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update review', variant: 'destructive' });
    },
  });

  const runAiModerationMutation = useMutation({
    mutationFn: async (reviewId: string) => {
      return apiRequest('POST', `/api/admin/reviews/${reviewId}/moderate-ai`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reviews'] });
      toast({ title: 'AI Moderation Complete', description: 'Review has been analyzed by AI' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to run AI moderation', variant: 'destructive' });
    },
  });

  const handleModerate = (reviewId: string, status: string) => {
    moderateMutation.mutate({ reviewId, status });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-green-500">Approved</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      case 'flagged':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Flagged</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredReviews = reviews?.filter(review => {
    if (statusFilter !== 'all' && review.moderationStatus !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        review.text?.toLowerCase().includes(query) ||
        review.user?.username.toLowerCase().includes(query) ||
        review.restaurant?.name.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Review Management</h1>
          <p className="text-muted-foreground">Moderate and manage user reviews</p>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search reviews..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search-reviews"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reviews</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReviews?.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  No reviews found
                </CardContent>
              </Card>
            ) : (
              filteredReviews?.map((review) => (
                <Card key={review.id} data-testid={`card-review-${review.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-medium">@{review.user?.username}</span>
                          <span className="text-muted-foreground">reviewed</span>
                          <span className="font-medium">{review.restaurant?.name}</span>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span>{review.rating}/10</span>
                          </div>
                          {getStatusBadge(review.moderationStatus)}
                          {reviewReportCounts.get(review.id) && reviewReportCounts.get(review.id)! > 0 && (
                            <Badge variant="destructive" className="flex items-center gap-1">
                              <Flag className="w-3 h-3" />
                              {reviewReportCounts.get(review.id)} report{reviewReportCounts.get(review.id)! !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {review.aiModerationRun && (
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge 
                                  variant={review.aiModerationFlagged ? "destructive" : "outline"}
                                  className="flex items-center gap-1"
                                >
                                  <Bot className="w-3 h-3" />
                                  {review.aiModerationFlagged ? 'AI Flagged' : 'AI OK'}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                <div className="text-xs space-y-1">
                                  <p className="font-medium">AI Moderation: {review.aiModerationAction}</p>
                                  {review.aiModerationReasons && review.aiModerationReasons.length > 0 && (
                                    <div>
                                      <p className="font-medium text-yellow-600">Reasons:</p>
                                      <ul className="list-disc pl-4">
                                        {review.aiModerationReasons.map((reason, i) => (
                                          <li key={i}>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {review.aiModerationAt && (
                                    <p className="text-muted-foreground">
                                      Checked: {new Date(review.aiModerationAt).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {review.text || 'No review text'}
                        </p>
                        {review.aiModerationRun && (
                          <Collapsible 
                            open={expandedReviews.has(review.id)} 
                            onOpenChange={() => toggleExpanded(review.id)}
                            className="mt-2"
                          >
                            <CollapsibleTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-auto py-1 px-2 text-xs w-full justify-between"
                                data-testid={`button-expand-ai-${review.id}`}
                              >
                                <span className="flex items-center gap-1">
                                  <Bot className="w-3 h-3" />
                                  AI Moderation Details
                                </span>
                                {expandedReviews.has(review.id) ? (
                                  <ChevronUp className="w-3 h-3" />
                                ) : (
                                  <ChevronDown className="w-3 h-3" />
                                )}
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className={`mt-2 p-3 rounded-md border ${
                                review.aiModerationFlagged 
                                  ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800' 
                                  : 'bg-muted/30 border-muted'
                              }`}>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2 text-sm font-medium">
                                    <Bot className="w-4 h-4" />
                                    AI Analysis Result: 
                                    <Badge variant={review.aiModerationFlagged ? 'destructive' : 'outline'}>
                                      {review.aiModerationAction?.toUpperCase()}
                                    </Badge>
                                  </div>
                                  {review.aiModerationAt && (
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(review.aiModerationAt).toLocaleString()}
                                    </span>
                                  )}
                                </div>

                                {review.aiModerationReasons && review.aiModerationReasons.length > 0 && (
                                  <div className="mb-3 p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
                                    <div className="flex items-center gap-1 text-red-700 dark:text-red-300 text-xs font-medium mb-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      Flagged Reasons:
                                    </div>
                                    <ul className="text-xs text-red-600 dark:text-red-400 pl-4 list-disc">
                                      {review.aiModerationReasons.map((reason, i) => (
                                        <li key={i}>{reason}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {review.aiModerationScores ? (
                                  <div className="space-y-2">
                                    <div className="text-xs font-medium text-muted-foreground mb-2">Category Scores:</div>
                                    <ScoreBar label="Hate" score={((review.aiModerationScores as AIModerationScores)?.text?.hate || 0)} />
                                    <ScoreBar label="Harassment" score={((review.aiModerationScores as AIModerationScores)?.text?.harassment || 0)} />
                                    <ScoreBar label="Violence" score={((review.aiModerationScores as AIModerationScores)?.text?.violence || 0)} />
                                    <ScoreBar label="Sexual" score={((review.aiModerationScores as AIModerationScores)?.text?.sexual || 0)} />
                                    <ScoreBar label="Self-harm" score={((review.aiModerationScores as AIModerationScores)?.text?.selfHarm || 0)} />
                                    <ScoreBar label="Profanity" score={((review.aiModerationScores as AIModerationScores)?.text?.profanity || 0)} />
                                    <ScoreBar label="Spam" score={((review.aiModerationScores as AIModerationScores)?.text?.spam || 0)} />
                                  </div>
                                ) : null}

                                {(review.aiModerationScores as AIModerationScores)?.images && (review.aiModerationScores as AIModerationScores).images!.length > 0 ? (
                                  <div className="mt-3 pt-3 border-t border-muted">
                                    <div className="text-xs font-medium text-muted-foreground mb-2">
                                      Image Analysis ({(review.aiModerationScores as AIModerationScores).images!.length} image{(review.aiModerationScores as AIModerationScores).images!.length > 1 ? 's' : ''}):
                                    </div>
                                    {(review.aiModerationScores as AIModerationScores)?.images?.map((img, idx) => (
                                      <div key={idx} className="flex flex-wrap gap-2 text-xs mb-1">
                                        <span className="text-muted-foreground">Image {idx + 1}:</span>
                                        <span className="font-medium">{img.flagged ? '🚩 Flagged' : '✅ Clean'}</span>
                                        {img.scores && (
                                          <div className="flex flex-wrap gap-1">
                                            {Object.entries(img.scores)
                                              .filter(([_, score]) => (score as number) > 0.1)
                                              .map(([category, score]) => (
                                                <span key={category} className="px-1 py-0.5 bg-muted rounded text-xs">
                                                  {category}: {Math.round((score as number) * 100)}%
                                                </span>
                                              ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(review.createdAt!).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 flex-shrink-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runAiModerationMutation.mutate(review.id)}
                              disabled={runAiModerationMutation.isPending}
                              data-testid={`button-ai-moderate-${review.id}`}
                            >
                              <RefreshCw className={`w-4 h-4 mr-1 ${runAiModerationMutation.isPending ? 'animate-spin' : ''}`} />
                              AI
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Re-run AI moderation</TooltipContent>
                        </Tooltip>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleModerate(review.id, 'approved')}
                          disabled={moderateMutation.isPending}
                          data-testid={`button-approve-${review.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleModerate(review.id, 'rejected')}
                          disabled={moderateMutation.isPending}
                          data-testid={`button-reject-${review.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleModerate(review.id, 'flagged')}
                          disabled={moderateMutation.isPending}
                          data-testid={`button-flag-${review.id}`}
                        >
                          <Flag className="w-4 h-4 mr-1" />
                          Flag
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
